<template>
<div class="dev-step-panel">
    <button
        v-if="collapsed"
        class="dev-step-toggle"
        @click="collapsed = false"
    >[Dev]</button>

    <div v-else class="dev-step-list">
        <div class="dev-step-header">
            <div class="dev-header-title">
                <span>{{ productName ?? 'unknown' }} ({{ steps.length }})</span>
                <button class="dev-step-close" @click="collapsed = true">&times;</button>
            </div>
            <div class="dev-header-toolbar">
                <div class="dev-toggles-column">
                    <label
                        class="dev-toggle"
                        title="Toggle the [Dev] Next button visibility on the form"
                    >
                        <span class="dev-toggle-switch" :class="{ 'dev-toggle-switch--on': !showRealButtons }">
                            <span class="dev-toggle-knob"></span>
                        </span>
                        <span class="dev-toggle-label">Dev Btn</span>
                        <input type="checkbox" v-model="showRealButtons" @change="$emit('toggle-real-buttons', showRealButtons)" />
                    </label>
                    <label
                        class="dev-toggle"
                        title="Stop auto-skipping hidden steps so you can inspect them"
                    >
                        <span class="dev-toggle-switch" :class="{ 'dev-toggle-switch--force': forceHidden }">
                            <span class="dev-toggle-knob"></span>
                        </span>
                        <span class="dev-toggle-label">Show Hidden</span>
                        <input type="checkbox" v-model="forceHidden" @change="$emit('toggle-force-hidden', forceHidden)" />
                    </label>
                    <label
                        class="dev-toggle"
                        title="Enforce field validation before allowing step advancement"
                    >
                        <span class="dev-toggle-switch" :class="{ 'dev-toggle-switch--validate': forceValidation }">
                            <span class="dev-toggle-knob"></span>
                        </span>
                        <span class="dev-toggle-label">Validate</span>
                        <input type="checkbox" v-model="forceValidation" @change="$emit('toggle-force-validation', forceValidation)" />
                    </label>
                    <label
                        class="dev-toggle"
                        title="When ON, dev navigation executes real server requests (validation, partials, submission)"
                    >
                        <span class="dev-toggle-switch" :class="{ 'dev-toggle-switch--allow': allowRequests }">
                            <span class="dev-toggle-knob"></span>
                        </span>
                        <span class="dev-toggle-label">Allow Requests</span>
                        <input type="checkbox" v-model="allowRequests" @change="$emit('toggle-allow-requests', allowRequests)" />
                    </label>
                </div>
                <span class="dev-toolbar-separator"></span>
                <div class="dev-toolbar-buttons">
                    <button class="dev-next-btn" title="Advance to the next step without validation" @click="$emit('next-step')">Next ▶</button>
                    <button class="dev-reset-btn" title="Clear all field values and go back to the first step (or the dev_step from the URL)" @click="resetPanel">↺ Reset</button>
                </div>
            </div>
        </div>
        <div class="dev-search-box">
            <input
                v-model="searchQuery"
                type="text"
                class="dev-search-input"
                placeholder="Search fields..."
            />
            <button
                v-if="searchQuery"
                class="dev-search-clear"
                @click="searchQuery = ''"
            >&times;</button>
        </div>
        <div class="dev-step-scroll">
            <template
                v-for="(step, index) in steps"
                :key="index"
            >
            <div v-if="isStepVisible(step, index)">
                <div
                    class="dev-step-row"
                    :class="{
                        'dev-step-active': index === activeStepIndex,
                        'dev-step-previous': index === previousStepIndex,
                        'dev-step-hidden': isStepHidden(step) && !(forceHidden && index === activeStepIndex),
                        'dev-step-forced': forceHidden && index === activeStepIndex && isStepHidden(step),
                    }"
                >
                    <span class="dev-step-index">{{ index + 1 }}</span>
                    <span class="dev-step-fields" @click="toggleExpand(index)">{{ getStepLabel(step) }}</span>
                    <span
                        v-if="isStepHidden(step)"
                        class="dev-step-badge"
                        :title="getConditionText(step.hideIfCondition)"
                    >hidden</span>
                    <button
                        class="dev-action-btn"
                        title="View fields"
                        @click.stop="toggleExpand(index)"
                    >{{ isExpanded(index) ? '▾' : '▸' }}</button>
                    <button
                        class="dev-action-btn dev-action-clear"
                        title="Clear field values"
                        @click.stop="emitClearStep(step)"
                    >✕</button>
                    <button
                        class="dev-action-btn dev-action-go"
                        title="Clear &amp; go to step"
                        @click.stop="emitClearAndJump(step, index)"
                    >→</button>
                </div>
                <div v-if="isStepHidden(step) && isExpanded(index)" class="dev-condition-text dev-condition-step">
                    visible when: {{ getConditionText(step.hideIfCondition) }}
                </div>

                <!-- Expandable field details for any step -->
                <div
                    v-if="isExpanded(index) && step.fields?.length"
                    class="dev-step-detail"
                    :class="{
                        'dev-step-detail--active': index === activeStepIndex,
                        'dev-step-detail--previous': index === previousStepIndex,
                    }"
                >
                    <div class="dev-step-detail-header">
                        <span>Fields</span>
                        <button class="dev-step-fill-btn" @click.stop="emitFillStep(step)">Fill</button>
                    </div>
                    <div
                        v-for="field in step.fields"
                        :key="field.name"
                    >
                        <div
                            class="dev-field-row"
                            :class="{
                                'dev-field-hidden': isFieldHidden(field),
                                'dev-field-match': isFieldMatch(field),
                            }"
                        >
                            <span class="dev-field-name">{{ field.name }}</span>
                            <span
                                class="dev-field-value"
                                :class="{ 'dev-field-value--stale': isFieldHidden(field) && !isValueEmpty(app[field.name]) }"
                            >{{ formatFieldValue(field.name) }}</span>
                            <span v-if="isFieldHidden(field) && !isValueEmpty(app[field.name])" class="dev-step-badge dev-badge-warn">has value</span>
                            <span v-else-if="isFieldHidden(field)" class="dev-step-badge">hidden</span>
                        </div>
                        <div v-if="isFieldHidden(field)" class="dev-condition-text">
                            visible when: {{ getConditionText(field.hideIfCondition) }}
                        </div>
                    </div>
                </div>
            </div>
            </template>
            <div v-if="isSearching && !hasSearchResults" class="dev-no-results">
                No fields matching "{{ searchQuery }}"
            </div>
        </div>
    </div>
</div>
</template>

<script setup>
import { ref, reactive, computed, inject, watch, nextTick } from 'vue';
import { generateDummyValue } from './devDummyData.js';

const props = defineProps({
    steps: {
        type: Array,
        required: true,
    },
    activeStepIndex: {
        type: Number,
        required: true,
    },
    productName: {
        type: String,
        default: null,
    },
});

const emit = defineEmits(['jump-to-step', 'fill-step', 'clear-step', 'toggle-real-buttons', 'next-step', 'toggle-force-hidden', 'toggle-force-validation', 'toggle-allow-requests', 'reset-form']);

const { app, config } = inject('app');
const collapsed = ref(false);
const showRealButtons = ref(false);
const forceHidden = ref(false);
const forceValidation = ref(false);
const allowRequests = ref(false);
const manualExpanded = reactive(new Set());
const manualCollapsed = reactive(new Set());
const previousStepIndex = ref(null);
const searchQuery = ref('');

const searchLower = computed(() => searchQuery.value.trim().toLowerCase());
const isSearching = computed(() => searchLower.value.length > 0);
const hasSearchResults = computed(() => props.steps.some((step, index) => isStepVisible(step, index)));

// Auto-expand only current and previous when active step changes
watch(() => props.activeStepIndex, (index, oldIndex) => {
    if (oldIndex !== undefined && oldIndex !== index) {
        previousStepIndex.value = oldIndex;
    }
    // Reset collapsed overrides so current and previous auto-expand on step change
    manualCollapsed.delete(index);
    if (previousStepIndex.value !== null) {
        manualCollapsed.delete(previousStepIndex.value);
    }
}, { immediate: true });

function isExpanded(index) {
    if (isSearching.value) {
        return stepHasMatchingField(props.steps[index]);
    }
    if (manualCollapsed.has(index)) {
        return false;
    }
    if (index === props.activeStepIndex || index === previousStepIndex.value) {
        return true;
    }
    return manualExpanded.has(index);
}

function toggleExpand(index) {
    // For auto-expanded steps (active/previous), use manualCollapsed
    if (index === props.activeStepIndex || index === previousStepIndex.value) {
        if (manualCollapsed.has(index)) {
            manualCollapsed.delete(index);
        } else {
            manualCollapsed.add(index);
        }
        return;
    }
    // For other steps, use manualExpanded
    if (manualExpanded.has(index)) {
        manualExpanded.delete(index);
    } else {
        manualExpanded.add(index);
    }
}

function stepHasMatchingField(step) {
    if (!step.fields) {
        return false;
    }
    return step.fields.some(f => f.name && f.name.toLowerCase().includes(searchLower.value));
}

function isStepVisible(step, index) {
    if (!isSearching.value) {
        return true;
    }
    // Show if step label or any field name matches
    const label = getStepLabel(step).toLowerCase();
    return label.includes(searchLower.value) || stepHasMatchingField(step);
}

function isFieldMatch(field) {
    if (!isSearching.value) {
        return false;
    }
    return field.name && field.name.toLowerCase().includes(searchLower.value);
}

function getStepLabel(step) {
    if (typeof step.title === 'function') {
        try {
            return step.title({ ...app.value }) || step.fields?.[0]?.name || '(dynamic title)';
        } catch {
            return step.fields?.[0]?.name || '(dynamic title)';
        }
    }
    if (step.title) {
        return step.title;
    }
    return step.fields?.[0]?.name || '(no fields)';
}

function isStepHidden(step) {
    if (typeof step.hideIfCondition === 'function') {
        try {
            return step.hideIfCondition({ ...app.value });
        } catch {
            return false;
        }
    }
    return false;
}

function isFieldHidden(field) {
    if (typeof field.hideIfCondition === 'function') {
        try {
            return field.hideIfCondition({ ...app.value });
        } catch {
            return false;
        }
    }
    return false;
}

function getConditionText(conditionFn) {
    if (typeof conditionFn !== 'function') {
        return String(conditionFn);
    }
    const src = conditionFn.toString();
    let body;
    const bodyMatch = src.match(/\{([\s\S]*)\}/);
    if (bodyMatch) {
        body = bodyMatch[1].trim();
    } else {
        const arrowMatch = src.match(/=>\s*([\s\S]+)$/);
        body = arrowMatch ? arrowMatch[1].trim() : src;
    }
    return body
        .replace(/[a-zA-Z_$][\w$]*__WEBPACK_IMPORTED_MODULE_\d+__\.\s*/g, '')
        .replace(/\(0,\s*/g, '(');
}

function formatFieldValue(fieldName) {
    const val = app.value[fieldName];
    if (val === null || val === undefined || val === '') {
        return '(empty)';
    }
    if (typeof val === 'object') {
        return JSON.stringify(val);
    }
    return String(val);
}

function isValueEmpty(val) {
    if (val === null || val === undefined || val === '') {
        return true;
    }
    if (typeof val === 'object' && !Array.isArray(val)) {
        return Object.values(val).every(v => v === null || v === undefined || v === '');
    }
    return false;
}

function getStepFieldNames(step) {
    return (step.fields || []).map(f => f.name).filter(Boolean);
}

function emitFillStep(step) {
    if (!step.fields) {
        return;
    }

    const values = {};
    for (const field of step.fields) {
        const currentVal = app.value[field.name];
        const isEmpty = isValueEmpty(currentVal);
        if (isEmpty && !isFieldHidden(field)) {
            const dummyVal = generateDummyValue(field, app.value, config.country);
            values[field.name] = dummyVal;

            // For fulladdress, set top-level fields + prepop_address so Address
            // shows manual fields and picks up values on remount
            if (field.type === 'fulladdress' && typeof dummyVal === 'object') {
                values['city'] = dummyVal.city;
                values['province'] = dummyVal.province;
                values['state'] = dummyVal.province;
                values['postal_code'] = dummyVal.postalCode;
                values['prepop_address'] = `${dummyVal.streetNumber} ${dummyVal.street}`;
            }
        }
    }

    if (Object.keys(values).length > 0) {
        emit('fill-step', values);
    }
}

function emitClearStep(step) {
    const fieldNames = getStepFieldNames(step);
    if (fieldNames.length > 0) {
        emit('clear-step', fieldNames);
    }
}

function resetPanel() {
    showRealButtons.value = false;
    emit('toggle-real-buttons', false);
    forceHidden.value = false;
    emit('toggle-force-hidden', false);
    forceValidation.value = false;
    emit('toggle-force-validation', false);
    allowRequests.value = false;
    emit('toggle-allow-requests', false);
    searchQuery.value = '';
    manualExpanded.clear();
    manualCollapsed.clear();
    emit('reset-form');
    // Clear after emit so the watcher triggered by activeStepIndex change doesn't override it
    nextTick(() => {
        previousStepIndex.value = null;
    });
}

function emitClearAndJump(step, index) {
    const fieldNames = getStepFieldNames(step);
    if (fieldNames.length > 0) {
        emit('clear-step', fieldNames);
    }
    emit('jump-to-step', index);
}
</script>

<style scoped>
.dev-step-panel {
    position: fixed;
    bottom: 16px;
    right: 16px;
    z-index: 9999;
    font-family: monospace;
    font-size: 12px;
}

.dev-step-toggle {
    background: rgba(30, 30, 30, 0.85);
    color: #ffc107;
    border: 1px solid #ffc107;
    border-radius: 4px;
    padding: 4px 10px;
    cursor: pointer;
    font-family: monospace;
    font-size: 12px;
}

.dev-step-list {
    background: rgba(30, 30, 30, 0.92);
    border: 1px solid #ffc107;
    border-radius: 6px;
    width: 340px;
    max-height: 50vh;
    display: flex;
    flex-direction: column;
    color: #eee;
}

.dev-step-header {
    padding: 0;
    border-bottom: 1px solid #555;
    color: #ffc107;
    font-weight: bold;
}

.dev-header-title {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 6px 10px 4px;
}

.dev-header-toolbar {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 4px 10px 6px;
}

.dev-toolbar-separator {
    width: 1px;
    align-self: stretch;
    background: #555;
    flex-shrink: 0;
}

.dev-toggle {
    display: flex;
    align-items: center;
    gap: 4px;
    cursor: pointer;
    user-select: none;
}

.dev-toggle input {
    display: none;
}

.dev-toggle-label {
    color: #aaa;
    font-weight: normal;
    font-size: 10px;
}

.dev-toggle-switch {
    position: relative;
    width: 26px;
    height: 14px;
    background: #555;
    border-radius: 7px;
    transition: background 0.2s;
}

.dev-toggle-switch--on {
    background: #ffc107;
}

.dev-toggle-knob {
    position: absolute;
    top: 2px;
    left: 2px;
    width: 10px;
    height: 10px;
    background: #1e1e1e;
    border-radius: 50%;
    transition: left 0.2s;
}

.dev-toggle-switch--on .dev-toggle-knob,
.dev-toggle-switch--force .dev-toggle-knob,
.dev-toggle-switch--validate .dev-toggle-knob,
.dev-toggle-switch--allow .dev-toggle-knob {
    left: 14px;
}

.dev-toggle-switch--force {
    background: #ff9800;
}

.dev-toggle-switch--validate {
    background: #2196f3;
}

.dev-toggle-switch--allow {
    background: #4caf50;
}

.dev-toggles-column {
    display: flex;
    flex-direction: column;
    gap: 3px;
}

.dev-toolbar-buttons {
    display: flex;
    flex-direction: row;
    gap: 3px;
}

.dev-next-btn {
    background: #ffc107;
    color: #1e1e1e;
    border: none;
    border-radius: 3px;
    padding: 2px 8px;
    font-family: monospace;
    font-size: 11px;
    font-weight: bold;
    cursor: pointer;
}

.dev-next-btn:hover {
    background: #ffca2c;
}

.dev-reset-btn {
    background: none;
    border: 1px solid #e57373;
    color: #e57373;
    border-radius: 3px;
    padding: 2px 8px;
    font-family: monospace;
    font-size: 11px;
    font-weight: bold;
    cursor: pointer;
}

.dev-reset-btn:hover {
    background: rgba(229, 115, 115, 0.2);
}

.dev-step-close {
    background: none;
    border: none;
    color: #ffc107;
    font-size: 16px;
    cursor: pointer;
    padding: 0 4px;
    line-height: 1;
}

.dev-search-box {
    position: relative;
    padding: 4px 10px;
    border-bottom: 1px solid #444;
}

.dev-search-clear {
    position: absolute;
    right: 14px;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    color: #888;
    font-size: 14px;
    cursor: pointer;
    padding: 0 4px;
    line-height: 1;
}

.dev-search-clear:hover {
    color: #ffc107;
}

.dev-search-input {
    width: 100%;
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid #555;
    border-radius: 3px;
    color: #eee;
    padding: 3px 6px;
    font-family: monospace;
    font-size: 11px;
    outline: none;
}

.dev-search-input:focus {
    border-color: #ffc107;
}

.dev-search-input::placeholder {
    color: #666;
}

.dev-step-scroll {
    overflow-y: auto;
    flex: 1;
}

.dev-no-results {
    padding: 12px 10px;
    color: #888;
    text-align: center;
    font-style: italic;
}

.dev-step-row {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px;
    border-left: 3px solid transparent;
}

.dev-step-row:hover {
    background: rgba(255, 255, 255, 0.1);
}

.dev-step-active {
    background: rgba(255, 193, 7, 0.15);
    border-left-color: #ffc107;
    font-weight: bold;
}

.dev-step-previous {
    background: rgba(100, 181, 246, 0.12);
    border-left-color: #64b5f6;
}

.dev-step-hidden {
    opacity: 0.45;
}

.dev-step-forced {
    background: rgba(255, 152, 0, 0.25);
    border-left-color: #ff9800;
    font-weight: bold;
}

.dev-step-index {
    min-width: 18px;
    text-align: right;
    color: #aaa;
}

.dev-step-fields {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    cursor: pointer;
}

.dev-step-badge {
    background: #555;
    color: #aaa;
    padding: 1px 5px;
    border-radius: 3px;
    font-size: 10px;
    flex-shrink: 0;
}

.dev-action-btn {
    background: none;
    border: 1px solid #555;
    color: #aaa;
    border-radius: 3px;
    padding: 0 4px;
    font-size: 11px;
    cursor: pointer;
    line-height: 1.4;
    flex-shrink: 0;
}

.dev-action-btn:hover {
    border-color: #ffc107;
    color: #ffc107;
}

.dev-action-clear:hover {
    border-color: #e57373;
    color: #e57373;
}

.dev-action-go:hover {
    border-color: #81c784;
    color: #81c784;
}

.dev-step-detail {
    padding: 4px 10px 6px 30px;
    border-left: 3px solid #555;
    background: rgba(255, 255, 255, 0.02);
}

.dev-step-detail--active {
    border-left-color: #ffc107;
    background: rgba(255, 193, 7, 0.05);
}

.dev-step-detail--previous {
    border-left-color: #64b5f6;
    background: rgba(100, 181, 246, 0.05);
}

.dev-step-detail-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    color: #888;
    font-size: 10px;
    margin-bottom: 2px;
    text-transform: uppercase;
}

.dev-step-fill-btn {
    background: #ffc107;
    color: #1e1e1e;
    border: none;
    border-radius: 3px;
    padding: 1px 8px;
    font-family: monospace;
    font-size: 10px;
    cursor: pointer;
    font-weight: bold;
}

.dev-step-fill-btn:hover {
    background: #ffca2c;
}

.dev-field-row {
    display: flex;
    align-items: baseline;
    gap: 6px;
    padding: 1px 0;
}

.dev-field-hidden {
    opacity: 0.4;
}

.dev-field-match {
    background: rgba(255, 193, 7, 0.2);
    border-radius: 2px;
    padding: 1px 2px;
}

.dev-field-name {
    color: #aaa;
    min-width: 80px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex-shrink: 0;
}

.dev-field-value {
    color: #7ec8e3;
    font-size: 11px;
    flex: 1;
    word-break: break-all;
}

.dev-field-value--stale {
    color: #ffb74d;
}

.dev-badge-warn {
    background: #e65100;
    color: #fff;
}

.dev-condition-text {
    color: #e57373;
    font-size: 10px;
    padding: 0 0 2px 0;
    word-break: break-all;
}

.dev-condition-step {
    padding-left: 30px;
    border-left: 3px solid transparent;
}
</style>
